import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Snackbar,
    Typography,
} from '@mui/material';
import ScheduleDrawer from '../zone/ScheduleDrawer';

const DEFAULT_ARMED_SCHEDULE = [
    { start: '00:00', stop: '23:59', days: [0, 1, 2, 3, 4, 5, 6] },
];

/**
 * Build a group-scoped URL for the metadata request.
 */
const buildUrlWithGroup = (baseUrl, groupName) => {
    try {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('group', groupName);
        return url.toString();
    } catch (error) {
        console.error('Invalid group zone arming URL', error);
        return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}group=${encodeURIComponent(groupName)}`;
    }
};

/**
 * Render the bulk zone arming drawer for one selected group.
 */
const GroupZoneArmingDrawer = ({ metaUrl, updateUrl }) => {
    const [open, setOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [itemsCount, setItemsCount] = useState(0);
    const [availableZones, setAvailableZones] = useState([]);
    const [selectedZones, setSelectedZones] = useState([]);
    const [metaLoading, setMetaLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [scheduleSeed, setScheduleSeed] = useState(DEFAULT_ARMED_SCHEDULE);

    const resetState = () => {
        setOpen(false);
        setGroupName('');
        setItemsCount(0);
        setAvailableZones([]);
        setSelectedZones([]);
        setMetaLoading(false);
        setSaving(false);
        setScheduleSeed(DEFAULT_ARMED_SCHEDULE.map((entry) => ({ ...entry, days: [...entry.days] })));
    };

    useEffect(() => {
        /**
         * Open the drawer after loading the group zone range.
         */
        const handler = async (event) => {
            const requestedGroup = String(event.detail?.groupName || '').trim();
            if (!requestedGroup) {
                return;
            }

            setOpen(true);
            setGroupName(requestedGroup);
            setItemsCount(0);
            setAvailableZones([]);
            setSelectedZones([]);
            setMetaLoading(true);
            setSaving(false);
            setScheduleSeed(DEFAULT_ARMED_SCHEDULE.map((entry) => ({ ...entry, days: [...entry.days] })));

            try {
                const response = await fetch(buildUrlWithGroup(metaUrl, requestedGroup), {
                    headers: {
                        Accept: 'application/json',
                    },
                });
                const payload = await response.json();

                if (!response.ok) {
                    throw new Error(payload.error || 'Failed to load the available zones for this group.');
                }

                setGroupName(payload.group_name || requestedGroup);
                setItemsCount(payload.items_count || 0);
                setAvailableZones(Array.isArray(payload.zones) ? payload.zones : []);
            } catch (error) {
                resetState();
                setAlert({
                    open: true,
                    message: error.message || 'Failed to load the available zones for this group.',
                    severity: 'error',
                });
            } finally {
                setMetaLoading(false);
            }
        };

        window.addEventListener('openGroupZoneArmingDrawer', handler);
        return () => window.removeEventListener('openGroupZoneArmingDrawer', handler);
    }, [metaUrl]);

    /**
     * Persist the selected schedule across the chosen zones in this group.
     */
    const handleSave = async (schedule) => {
        if (!selectedZones.length) {
            setAlert({
                open: true,
                message: 'Select at least one zone before applying the schedule.',
                severity: 'warning',
            });
            return false;
        }

        setSaving(true);

        try {
            const response = await fetch(updateUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': window.csrfToken || document.querySelector('input[name="_token"]')?.value || '',
                },
                body: JSON.stringify({
                    group_name: groupName,
                    zone_indexes: selectedZones,
                    armed: schedule,
                }),
            });
            const payload = await response.json();

            if (!response.ok) {
                throw new Error(payload.error || 'Failed to update zone arming for this group.');
            }

            const details = [];
            if (payload.updated_zone_instances !== undefined) {
                details.push(`Updated zones: ${payload.updated_zone_instances}`);
            }
            if (payload.updated_items !== undefined) {
                details.push(`Updated fluxes: ${payload.updated_items}`);
            }
            if (payload.stopped_fluxes) {
                details.push(`Stopped fluxes: ${payload.stopped_fluxes}`);
            }
            if (Array.isArray(payload.warnings) && payload.warnings.length) {
                details.push(`Warnings: ${payload.warnings.join(' | ')}`);
            }

            setAlert({
                open: true,
                message: `${payload.message || 'Zone arming updated successfully.'}${details.length ? `\n${details.join('\n')}` : ''}`,
                severity: Array.isArray(payload.warnings) && payload.warnings.length ? 'warning' : 'success',
            });
            resetState();
            return true;
        } catch (error) {
            setAlert({
                open: true,
                message: error.message || 'Failed to update zone arming for this group.',
                severity: 'error',
            });
            return false;
        } finally {
            setSaving(false);
        }
    };

    const headerContent = useMemo(() => {
        return (
            <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Apply one armed schedule to the selected zone numbers across this group.
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Only the selected zones will be updated. All published fluxes touched by this change will be stopped. Restart them manually afterward if needed.
                </Alert>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Fluxes that do not have a selected zone are skipped automatically.
                </Alert>
                <FormControl fullWidth disabled={metaLoading || saving || availableZones.length === 0}>
                    <InputLabel id="group-zone-arming-select-label">Zones</InputLabel>
                    <Select
                        labelId="group-zone-arming-select-label"
                        multiple
                        value={selectedZones}
                        onChange={(event) => setSelectedZones(event.target.value.map((value) => Number(value)).sort((left, right) => left - right))}
                        input={<OutlinedInput label="Zones" />}
                        renderValue={(selected) => selected.map((zoneNumber) => `Zone ${zoneNumber}`).join(', ')}
                    >
                        {availableZones.map((zoneNumber) => (
                            <MenuItem key={zoneNumber} value={zoneNumber}>
                                <Checkbox checked={selectedZones.includes(zoneNumber)} />
                                <ListItemText primary={`Zone ${zoneNumber}`} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, mb: 1 }}>
                    <Button size="small" onClick={() => setSelectedZones([...availableZones])} disabled={metaLoading || saving || availableZones.length === 0}>
                        Select all
                    </Button>
                    <Button size="small" onClick={() => setSelectedZones([])} disabled={metaLoading || saving || selectedZones.length === 0}>
                        Clear
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {metaLoading
                        ? 'Loading the available zone range for this group...'
                        : availableZones.length === 0
                            ? 'This group has no configured zones yet.'
                            : `Group contains ${itemsCount} fluxes and exposes zones 1-${availableZones[availableZones.length - 1]}.`}
                </Typography>
            </>
        );
    }, [availableZones, itemsCount, metaLoading, saving, selectedZones]);

    return (
        <>
            <ScheduleDrawer
                open={open}
                onClose={() => {
                    if (!saving) {
                        resetState();
                    }
                }}
                initialSchedule={scheduleSeed}
                onSave={handleSave}
                title={`Bulk Zone Arming (${groupName})`}
                headerContent={headerContent}
                saveLabel="Apply to Group"
                disabled={metaLoading || saving || availableZones.length === 0}
            />
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={() => setAlert((previous) => ({ ...previous, open: false }))}
            >
                <Alert
                    onClose={() => setAlert((previous) => ({ ...previous, open: false }))}
                    severity={alert.severity}
                    sx={{ width: '100%', whiteSpace: 'pre-line' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default GroupZoneArmingDrawer;
